import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from './';
import { 
  login, 
  logout, 
  register, 
  verify2FA,
  refreshToken,
  loadUserData,
  forgotPassword,
  resetPassword,
  clearError,
  clear2FA
} from '@/store/slices/authSlice';
import { LoginFormData, RegisterFormData } from '@/types';

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const auth = useAppSelector(state => state.auth);

  const handleLogin = useCallback(async (credentials: LoginFormData) => {
    return dispatch(login(credentials));
  }, [dispatch]);

  const handleRegister = useCallback(async (userData: RegisterFormData) => {
    return dispatch(register(userData));
  }, [dispatch]);

  const handleLogout = useCallback(async () => {
    return dispatch(logout());
  }, [dispatch]);

  const handleVerify2FA = useCallback(async (code: string) => {
    if (!auth.twoFactorToken) {
      throw new Error('Token 2FA não encontrado');
    }
    return dispatch(verify2FA({ code, twoFactorToken: auth.twoFactorToken }));
  }, [dispatch, auth.twoFactorToken]);

  const handleRefreshToken = useCallback(async () => {
    return dispatch(refreshToken());
  }, [dispatch]);

  const handleLoadUserData = useCallback(async () => {
    return dispatch(loadUserData());
  }, [dispatch]);

  const handleForgotPassword = useCallback(async (email: string) => {
    return dispatch(forgotPassword(email));
  }, [dispatch]);

  const handleResetPassword = useCallback(async (token: string, password: string) => {
    return dispatch(resetPassword({ token, password }));
  }, [dispatch]);

  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleClear2FA = useCallback(() => {
    dispatch(clear2FA());
  }, [dispatch]);

  // Verificar se usuário pode fazer login (rate limiting)
  const canAttemptLogin = useCallback(() => {
    if (auth.loginAttempts < 5) return true;
    
    if (auth.lastLoginAttempt) {
      const timeSinceLastAttempt = Date.now() - auth.lastLoginAttempt;
      const lockoutTime = 15 * 60 * 1000; // 15 minutos
      return timeSinceLastAttempt > lockoutTime;
    }
    
    return false;
  }, [auth.loginAttempts, auth.lastLoginAttempt]);

  // Tempo restante para próxima tentativa
  const getTimeUntilNextAttempt = useCallback(() => {
    if (auth.loginAttempts < 5 || !auth.lastLoginAttempt) return 0;
    
    const timeSinceLastAttempt = Date.now() - auth.lastLoginAttempt;
    const lockoutTime = 15 * 60 * 1000; // 15 minutos
    const timeRemaining = lockoutTime - timeSinceLastAttempt;
    
    return Math.max(0, timeRemaining);
  }, [auth.loginAttempts, auth.lastLoginAttempt]);

  return {
    // Estado
    user: auth.user,
    company: auth.company,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    error: auth.error,
    twoFactorRequired: auth.twoFactorRequired,
    loginAttempts: auth.loginAttempts,

    // Ações
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    verify2FA: handleVerify2FA,
    refreshToken: handleRefreshToken,
    loadUserData: handleLoadUserData,
    forgotPassword: handleForgotPassword,
    resetPassword: handleResetPassword,
    clearError: handleClearError,
    clear2FA: handleClear2FA,

    // Utilitários
    canAttemptLogin,
    getTimeUntilNextAttempt,
  };
};